'use strict'

import State from 'ampersand-state'
import Collection from 'ampersand-rest-collection'
import {RoomCollection, RoomModel} from './greenroom-room-model'
import DeviceModel from './greenroom-device-model'
import * as mediasoupClient from 'mediasoup-client'
import Observable from './_observable'
import flow from 'lodash/flow'
import Logger from './logger'
import {assign, get, debounce, map, each, flatten, find, defaultsDeep, extend, result, pick, filter, groupBy} from 'lodash/fp'

const CHROME_EXTENSION_EL_ID = 'infiniscene-extension-installed'

// TODO: This logger is identical to studio logger and should be made globally accessible
const logger = Logger.create('Greenroom', ['webrtc', 'greenroom'])

const WebRTC = State.extend(Observable).extend({
  props: {
    id: 'string',
    defaultPeerId: 'string',
    extensionUrl: 'string',
    defaultIsHost: ['boolean', true, false],
    device: ['object', true, () => mediasoupClient.getDeviceInfo()]
  },
  collections: {
    rooms: RoomCollection,
    webcams: Collection.extend({model: DeviceModel}),
    microphones: Collection.extend({model: DeviceModel}),
    speakers: Collection.extend({model: DeviceModel})
  },
  derived: {},
  initialize () {
    window.addEventListener('message', this._handleExtensionMessage.bind(this))
    this.logger = logger
  },
  ensureRoom (id, settings = {}) {
    this.logger.debug('ensureRoom()')
    let room = this.rooms.get(id)
    if (!room) {
      room = new RoomModel(assign({
        id,
        logger: this.logger,
        manager: this,
        peerId: this.defaultPeerId,
        isHost: this.defaultIsHost
      }, settings))
      this.rooms.add(room)
    }
    return room
  },
  async joinRoom (id, settings = {}) {
    try {
      this.logger.debug('joinRoom()')
      const room = this.ensureRoom(id, settings)
      room.open()
      this.activeRoom = room
    } catch (e) {
      this.logger.warn('An error occurred while joining the greenroom', {room: id})
      throw e
    }
  },
  async leaveRoom (id) {
    try {
      this.logger.debug('leaveRoom()')
      const room = this.rooms.get(id)
      room.leave()
      this.activeRoom = null
    } catch (e) {
      this.logger.warn('An error occurred while leaving the greenroom', {room: id})
      this.logger.error(e)
    }
  },

  // UserMedia helpers
  async getUserMedia (constraints, ...args) {
    this.logger.info(`getUserMedia()`, ...arguments)
    return navigator.mediaDevices
      .getUserMedia(constraints, ...args)
      .catch(err => {
        console.warn('Could not GUM!', JSON.stringify(args), err)
        // TODO: How do we figure out which device failed the GUM?
      })
  },
  async updateDevices () {
    this.logger.info(`updateDevices()`, ...arguments)
    this.trigger('devices:updating')
    try {
      // Once we get permissions, we will update devices again to extract their labels
      await this.ensurePermissionState()
    } catch (e) {}
    const getWebcamType = x => /(back|rear)/i.test(x.label) ? 'back' : 'front'
    const extendWebcamType = x => x.kind === 'videoinput' ? extend({type: getWebcamType(x)})(x) : x
    const extendDetails = x => extend({id: x.deviceId, webrtc: this, logger: this.logger})(x)
    const devices = flow(
      map(extendWebcamType),
      map(extendDetails),
      groupBy('kind')
    )(await navigator.mediaDevices.enumerateDevices())

    this.microphones.set(devices.audioinput)
    this.speakers.set(devices.audiooutput)
    this.webcams.set(devices.videoinput)
    return this.ensurePermissionState()
  },
  getDeviceByIdAndType (deviceId, type) {
    const collection = type === 'webcam' ? this.webcams : this.microphones
    return collection.find({deviceId})
  },
  getActiveOrFirstWebcam () {
    return this.webcams.find(get('track')) || this.webcams.first()
  },
  getActiveOrFirstMicrophone () {
    return this.microphones.find(get('track')) || this.microphones.first()
  },
  async ensurePermissionState () {
    const [firstWebcam, firstMicrophone] = [this.webcams.first(), this.microphones.first()]
    let permissionsNeeded = {}
    permissionsNeeded.video = !(firstWebcam && firstWebcam.label)
    permissionsNeeded.audio = !(firstMicrophone && firstMicrophone.label)
    if (!permissionsNeeded.audio && !permissionsNeeded.video) return true
    try {
      const stream = await this.getUserMedia(permissionsNeeded)
      stream && stream.getTracks().forEach(track => {
        delete permissionsNeeded[track.kind]
        if (track.kind === 'video') {
          if (this.webcams.find(x => x.track === track)) return
          track.stop()
        }
        if (track.kind === 'audio') {
          if (this.microphones.find(x => x.track === track)) return
          track.stop()
        }
      })
      return Object.keys(permissionsNeeded).length === 0
    } catch (err) {
      if (err.name === 'PermissionDeniedError' || err.name === 'NotAllowedError') {
        return false
      }
      throw err
    }
  },

  // Browser extension (screenshare)
  checkUserHasExtension () {
    return !!document.getElementById(CHROME_EXTENSION_EL_ID)
  },
  async openExtensionDownloadPage () {
    try {
      await new Promise((resolve, reject) => {
        chrome.webstore.install(this.extensionUrl, () => resolve(true), err => reject(new Error(err)))
      })
      return true
    } catch (e) {
      return await new Promise((resolve) => {
        window.open(this.extensionUrl)
        window.addEventListener('focus', () => {
          resolve(this.checkUserHasExtension())
        })
      })
    }
  },
  async openBrowserWindowSelection (selectionOptions = {}) {
    const {audio = true, screen = true} = selectionOptions
    if (!this.checkUserHasExtension()) {
      const err = new Error('Extension required for browser window selection')
      err.name = 'ExtensionRequired'
      throw err
    }
    let selectionItems
    if (screen) {
      selectionItems = ['screen', 'window', 'tab']
    }
    if (audio) {
      if (!selectionItems) {
        selectionItems = ['audio', 'screen']
      } else {
        selectionItems.push('audio')
      }
    }
    if (!selectionItems) throw new Error('Must enable either audio or screen selection')

    const {streamId, options} = await this.sendGetScreenMessage(selectionItems)
    if (!streamId) {
      const err = new Error('User canceled capture selection')
      err.name = 'SelectionCanceled'
      throw err
    }

    const constraints = {
      mandatory: {
        chromeMediaSource: 'desktop',
        chromeMediaSourceId: streamId
      }
    }
    const stream = await this.getUserMedia({
      audio: options.canRequestAudioTrack && audio ? constraints : false,
      video: constraints
    })
    if (!stream) {
      const err = new Error('getUserMedia did not return a stream under the constraints provided')
      err.name = 'EmptyResponse'
      throw err
    }

    const audioTrack = stream.getAudioTracks()[0]
    const videoTrack = stream.getVideoTracks()[0]
    const result = {}

    if (audioTrack) {
      result.audio = {
        type: 'audio',
        deviceId: 'browser',
        displayName: 'System audio',
        track: audioTrack
      }
    }
    if (videoTrack) {
      if (!screen) {
        videoTrack.stop()
      } else {
        const screenType = videoTrack.label.substr(0, videoTrack.label.indexOf(':'))
        result.video = {
          type: 'screen',
          deviceId: streamId,
          displayName: `Browser ${screenType}`,
          track: videoTrack,
          screenType
        }
      }
    }
    return result
  },
  sendGetScreenMessage (options) {
    const messageId = _.uniqueId()
    return new Promise(resolve => {
      this.once(`extension:message:${messageId}`, resolve)
      window.postMessage({type: 'infinisceneGetScreen', messageId, options}, '*')
    })
  },
  _handleExtensionMessage (event) {
    if (event.data && event.data.type === 'infinisceneGotScreen') {
      this.trigger(`extension:message:${event.data.messageId}`, event.data)
    }
  }
})

export default new WebRTC()



// WEBPACK FOOTER //
// ./client/webrtc/webrtc-manager.js